import React from 'react';
import { Accordion, AccordionItem } from 'is24-corecss';
import { css } from '@emotion/css';
import { APP_CONFIG } from '../../../config';
import { useMembership } from '../../../customHooks/useMembership';
import { useProducts } from '../../../customHooks/useProducts';
import { useGetBenefitsListPage } from '../../../customHooks/useGetBenefitsListPage.hook';
import { BenefitsListPage } from '../../../@types/benefitsListPage';
import { useGetHasProduct } from '../../../customHooks/useGetHasProduct.hook';
import { usePremiumSubscriptionEntitlement } from '../../../customHooks/useGetHasEntitlement.hook';
import { ProductType } from '../../../@types/product';
import { Msg, useIntl } from '@/i18n';
import { FormattedMessageDictionary } from '@/@types/FormattedMessageValue';

const styles = {
  lastAccordionItem: {
    borderBottom: 'none',
  },
};

type FAQListItemProps = {
  id: string;
  defaultMessage: string;
  values?: FormattedMessageDictionary;
};

const FAQlistItemContent = ({ id, defaultMessage, values = {} }: FAQListItemProps) => {
  return (
    <Msg
      key={id}
      id={id}
      defaultMessage={defaultMessage}
      values={{
        ...{
          br: <br />,
          ul: (children) => <ul className='list-bullet'>{children}</ul>,
          li: (children) => (
            <li style={{ listStyleType: 'none', marginLeft: 0 }}>
              <i className='is24-icon-positive-check' />
              {children}
            </li>
          ),
          strong: (chunks) => <strong>{chunks}</strong>,
          p: (chunks) => <p>{chunks}</p>,
        },
        ...values,
      }}
    />
  );
};

export const FAQ = () => {
  const { t } = useIntl();
  const { contractSource = null } = useMembership();
  const { products } = useProducts();
  const isEntitledLivingPlus = useGetHasProduct(ProductType.WohnenPlus);
  const isEntitledMieterPlus = useGetHasProduct(ProductType.MieterPlus);
  const benefitsListPage = useGetBenefitsListPage();
  const isPremiumSubscriptionEntitled = usePremiumSubscriptionEntitlement();

  const manageSubLink = isPremiumSubscriptionEntitled
    ? '/meinkonto/einstellungen/premiumprofil?cmp_id=10-051401&cmp_name=settings_profile&cmp_position=residential_benefits&cmp_creative=settings_cta'
    : '/vertragsuebersicht?cmp_id=10-051401&cmp_name=settings_profile&cmp_position=residential_benefits&cmp_creative=settings_cta';

  const defaultSource = products?.length ? 'CHECKOUT' : 'MYSCOUT';
  const contractOverviewUrl =
    APP_CONFIG.contractOverviewUrl[contractSource ?? defaultSource] ?? APP_CONFIG.contractOverviewUrl[defaultSource];

  const faqUrl = APP_CONFIG.faqUrl;
  const isKauferPlusPage = benefitsListPage === BenefitsListPage.KauferPlus;
  const isMieterPlusPage = benefitsListPage === BenefitsListPage.MieterPlus;
  const isLivingBundlePage = benefitsListPage === BenefitsListPage.WohnenPlus;

  const copyPrefix = React.useMemo(() => {
    if (isKauferPlusPage) return 'kaeuferPlus';
    if (isEntitledMieterPlus && isMieterPlusPage) return 'mieterPlus';
    if (isEntitledLivingPlus && isLivingBundlePage) return 'livingBundle';
    return 'premium';
  }, [benefitsListPage, isEntitledLivingPlus, isEntitledMieterPlus]);

  return (
    <div id='FAQ'>
      <h2 className='font-heading-small-regular'>
        <Msg id='faq.title' defaultMessage='Membership Q&A' />
      </h2>
      <Accordion>
        <AccordionItem
          key={`faq.accordion.section1.${copyPrefix}.title`}
          title={t({ id: `faq.accordion.section1.${copyPrefix}.title`, defaultMessage: 'How to cancel membership...' })}
        >
          <FAQlistItemContent
            key={`faq.accordion.section1.${copyPrefix}.description`}
            id={`faq.accordion.section1.${copyPrefix}.description`}
            defaultMessage='You can cancel...'
            values={{
              Link: (
                <a
                  title={t({
                    id: 'faq.accordion.section1.linkTitle',
                    defaultMessage: 'Cancel membership link title...',
                  })}
                  href={isKauferPlusPage ? contractOverviewUrl : manageSubLink}
                >
                  <Msg id='faq.accordion.section1.linkText' defaultMessage='Link to contract overview...' />
                </a>
              ),
            }}
          />
        </AccordionItem>
        section2
        <AccordionItem
          key={`faq.accordion.section2.${copyPrefix}.title`}
          title={t({
            id: `faq.accordion.section2.${copyPrefix}.title`,
            defaultMessage: 'Where to find pricing and...',
          })}
        >
          <FAQlistItemContent
            key={`faq.accordion.section2.${copyPrefix}.description`}
            id={`faq.accordion.section2.${copyPrefix}.description`}
            defaultMessage='Find pricing and more details...'
            values={
              isKauferPlusPage
                ? {
                    Link: (
                      <a
                        title={t({
                          id: 'faq.accordion.section2.kaeuferPlus.linkTitle',
                          defaultMessage: 'See pricing link title...',
                        })}
                        href={contractOverviewUrl}
                      >
                        <Msg
                          id='faq.accordion.section2.kaeuferPlus.linkText'
                          defaultMessage='Link to contract overview...'
                        />
                      </a>
                    ),
                  }
                : undefined
            }
          />
        </AccordionItem>
        <AccordionItem
          key={`faq.accordion.section3.${copyPrefix}.title`}
          title={t({ id: `faq.accordion.section3.${copyPrefix}.title`, defaultMessage: 'Is an invoice available...' })}
          titleClassName={css(styles.lastAccordionItem)}
        >
          <FAQlistItemContent
            key={`faq.accordion.section3.${copyPrefix}.description`}
            id={`faq.accordion.section3.${copyPrefix}.description`}
            defaultMessage='We cannot issue an invoice...'
            values={isKauferPlusPage ? { Br: <br /> } : undefined}
          />
        </AccordionItem>
      </Accordion>
      <div className='with-icon'>
        <i className='is24-icon-chevron-right' />
        <a title={t({ id: 'faq.linkTitle', defaultMessage: 'See FAQs for search...' })} href={faqUrl}>
          <Msg id='faq.linkText' defaultMessage='Link to FAQs for search...' />
        </a>
      </div>
    </div>
  );
};
